import ServiceBase from './serviceBase';
import {
  GrievanceBasicItem,
  GrievanceCommentExist,
  GrievanceDetailsData,
  GrievanceFilter,
  GrievanceForm,
  GrievanceFormContextType,
  GrievanceItem,
  GrievanceList,
  GrievanceLogbookForm,
  GrievanceLogbookItem,
  GrievancePhase,
  GrievancePhaseDataApply,
  GrievanceSaveResult,
} from './types/Grievance';

class GrievanceService extends ServiceBase {
  exportExcel = async (filter: GrievanceFilter): Promise<boolean> => {
    await this.download('grievances/excel', filter);
    return true;
  };

  getGrievanceFilters = async (): Promise<number[]> => this.get<number[]>('grievances/filters');

  getGrievances = async (filter: GrievanceFilter): Promise<GrievanceList> =>
    this.post<GrievanceList>('grievances/search', filter);

  getGrievanceFormContext = async (): Promise<GrievanceFormContextType> =>
    this.get<GrievanceFormContextType>(`grievances/grievanceformContext`);

  saveGrievance = async (payload: GrievanceForm): Promise<GrievanceSaveResult> =>
    this.post<GrievanceSaveResult>('grievances/save', payload);

  getGrievanceList = async (filter: {
    query: string;
    onlyClosed?: boolean;
  }): Promise<GrievanceBasicItem[]> => {
    if (!filter.query || !filter.query.trim()) {
      return [];
    }
    const closed = filter.onlyClosed ? '?onlyClosed=true' : '';
    return this.get<GrievanceBasicItem[]>(`grievances/searchbynumber/${filter.query}${closed}`);
  };

  getGrievanceByNumber = async (number: string): Promise<GrievanceForm> =>
    this.get<GrievanceForm>(`grievances/bynumber/${number}`);

  getTimeline = async (grievanceNumber: string): Promise<GrievancePhase[]> =>
    this.get<GrievancePhase[]>(`grievances/grievance/${grievanceNumber}/timeline`);

  getGrievance = async (number: string): Promise<GrievanceItem> =>
    this.get<GrievanceItem>(`grievances/grievance/${number}`);

  getGrievanceDetailsContext = async (grievanceNumber: string): Promise<GrievanceDetailsData> =>
    this.get<GrievanceDetailsData>(`grievances/grievance/${grievanceNumber}/details-context`);

  getGrievancePhases = async (grievanceNumber: string): Promise<GrievancePhase[]> =>
    this.get<GrievancePhase[]>(`grievances/grievance/${grievanceNumber}/phases`);

  moveToNextPhase = async (grievanceNumber: string): Promise<void> =>
    this.post<void>(`grievances/grievance/${grievanceNumber}/move-to-next-phase`, {});

  discontinueManagement = async (grievanceNumber: string): Promise<void> =>
    this.post<void>(`grievances/grievance/${grievanceNumber}/discontinue-management`, {});

  phaseApply = async (
    grievanceNumber: string,
    grievancePhase: GrievancePhase,
    dataApply: GrievancePhaseDataApply
  ): Promise<void> =>
    this.post<void>(`grievances/grievance/${grievanceNumber}/phase-apply`, {
      phase: grievancePhase,
      ...dataApply,
    });

  logbook = async (
    grievanceNumber: string,
    logbookData: GrievanceLogbookForm
  ): Promise<GrievanceSaveResult> => {
    const formData = new FormData();

    formData.append('grievanceNumber', grievanceNumber);
    formData.append('phaseId', logbookData.phaseId.toString());
    formData.append('documentTypeId', (logbookData.documentTypeId || 0).toString());
    formData.append('comment', logbookData.comment);
    formData.append('fromTemplate', logbookData.fromTemplate.toString());
    formData.append(
      'overrideComment',
      logbookData.overrideComment === undefined || logbookData.overrideComment === null
        ? 'false'
        : logbookData.overrideComment.toString()
    );

    if (logbookData.formData) {
      const fileForUpload = logbookData.formData.get('file') as File;
      if (fileForUpload instanceof File) {
        formData.append('file', fileForUpload);
      }
    }

    return this.post<GrievanceSaveResult>(`grievances/logbook/${grievanceNumber}`, formData);
  };

  removeComment = async (grievanceNumber: string, commentId: number): Promise<void> =>
    this.delete<void>(`grievances/logbook/${grievanceNumber}/${commentId}`);

  saveLogbookItem = async (
    grievanceNumber: string,
    logbookItem: GrievanceLogbookItem
  ): Promise<void> =>
    this.put<void>(`grievances/logbook/${grievanceNumber}/${logbookItem.id}`, {
      id: logbookItem.id,
      phaseId: logbookItem.phaseId,
      documentTypeId: logbookItem.documentTypeId,
      comments: logbookItem.comments,
    });

  canSave = async (grievanceNumber: string, documentName: string): Promise<GrievanceCommentExist> =>
    this.post<GrievanceCommentExist>(`grievances/logbook/${grievanceNumber}/can-save`, {
      documentName,
    });

  saveIsFinalFlag = async (
    grievanceNumber: string,
    logbookItemId: number,
    isFinal: boolean
  ): Promise<void> =>
    this.put<void>(`grievances/logbook/${grievanceNumber}/${logbookItemId}/is-final`, {
      id: logbookItemId,
      isFinal,
    });

  downloadDocumentComment = async (
    grievanceNumber: string,
    commentId: number
  ): Promise<boolean> => {
    await this.download(`grievances/logbook/${grievanceNumber}/${commentId}/download`);
    return true;
  };

  checkAccessToGrievances = async (
    grievanceNumber?: string,
    formAccess?: boolean
  ): Promise<boolean> =>
    this.get<boolean>(
      `grievances/check-access?grievanceNumber=${grievanceNumber || ''}&formAccess=${
        formAccess || 'false'
      }`
    );

  deleteGrievance = async (grvId: number): Promise<boolean> => {
    await this.delete(`grievances/${grvId}`);
    return true;
  };
}

const grievanceService = new GrievanceService();

export default grievanceService;
